import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bookmark, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  deleteSavedSearch,
  listSavedSearchesForSource,
  type SavedSearch,
} from '@/db/savedSearches';
import { requestAccountDataAutoSync } from '@/features/account/accountDataAutoSync';

export interface SavedSearchesMenuProps {
  /** `'*'` for cross-source, otherwise the source id. */
  scope: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Modal listing saved searches for the given scope. Each entry navigates
 * via React Router; deletion is in-place with optimistic UI.
 */
export function SavedSearchesMenu({ scope, open, onOpenChange }: SavedSearchesMenuProps) {
  const [items, setItems] = useState<SavedSearch[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setItems(null);
    setError(null);
    listSavedSearchesForSource(scope)
      .then((rows) => {
        if (!cancelled) setItems(rows);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [open, scope]);

  async function handleDelete(id: string) {
    try {
      await deleteSavedSearch(id);
      requestAccountDataAutoSync();
      setItems((cur) => (cur ? cur.filter((r) => r.id !== id) : cur));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bookmark className="h-4 w-4" /> Saved searches
          </DialogTitle>
          <DialogDescription>
            Click an entry to load it. Signed-in accounts back up saved entries for restore
            on other devices.
          </DialogDescription>
        </DialogHeader>
        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-sm">
            {error}
          </div>
        )}
        {items === null ? (
          <div className="py-4 text-sm text-muted-foreground">Loading…</div>
        ) : items.length === 0 ? (
          <div className="py-4 text-sm text-muted-foreground">
            No saved searches yet. Use the &quot;Save&quot; button next to the search box to
            add one.
          </div>
        ) : (
          <ul className="flex flex-col gap-1">
            {items.map((row) => (
              <li
                key={row.id}
                className="flex items-center justify-between gap-2 rounded-md border border-border/40 bg-card/30 px-2 py-1.5"
              >
                <Link
                  to={row.url}
                  onClick={() => onOpenChange(false)}
                  className="flex-1 truncate text-sm hover:underline"
                  title={row.url}
                >
                  {row.name}
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(row.id)}
                  aria-label={`Delete ${row.name}`}
                  className="h-7 w-7 p-0"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

export interface SaveSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Default name suggested in the input — typically the keyword. */
  suggestedName: string;
  /** Called when the user clicks Save. */
  onSave: (name: string) => Promise<void> | void;
}

/**
 * Modal prompting for a saved-search name. Separate from the listing modal
 * so the two dialogs can be opened independently.
 */
export function SaveSearchDialog({
  open,
  onOpenChange,
  suggestedName,
  onSave,
}: SaveSearchDialogProps) {
  const [name, setName] = useState(suggestedName);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(suggestedName);
      setError(null);
      setSaving(false);
    }
  }, [open, suggestedName]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(name.trim());
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save search</DialogTitle>
          <DialogDescription>
            Give this search a name. You&apos;ll be able to recall it from the &quot;Saved&quot;
            menu.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="My saved search"
            autoFocus
            className="h-9 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={saving || !name.trim()}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
